import { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/layout/Layout";
import SEO from "@/components/SEO";

import gallery1 from "@/assets/gallery-1.jpg";
import gallery2 from "@/assets/gallery-2.jpg";
import gallery3 from "@/assets/gallery-3.jpg";
import gallery4 from "@/assets/gallery-4.jpg";
import gallery5 from "@/assets/gallery-5.jpg";
import gallery6 from "@/assets/gallery-6.jpg";
import bottleButterfly from "@/assets/bottle-butterfly.jpg";
import bottleGuitar from "@/assets/bottle-guitar.jpg";
import bottleSilverHeart from "@/assets/bottle-silver-heart.jpg";
import bottleCouple from "@/assets/bottle-couple.jpg";
import bottlePurpleHeart from "@/assets/bottle-purple-heart.jpg";
import bottleGoldNames from "@/assets/bottle-gold-names.jpg";
import bottleRedHearts from "@/assets/bottle-red-hearts.jpg";
import bottleCollection from "@/assets/bottle-collection.jpg";

const galleryItems = [
  { src: bottleButterfly, title: "Rainbow Butterfly Bottle", desc: "Vibrant jewelled butterfly with personalised birthday message.", category: "bottle" },
  { src: bottleGuitar, title: "Rock Guitar Bottle", desc: "Gothic-style guitar with bat wings and spiked collar detail.", category: "bottle" },
  { src: bottleSilverHeart, title: "Silver Heart & Flowers Bottle", desc: "Elegant silver filigree heart with floral design and ribbon bow.", category: "bottle" },
  { src: bottleCouple, title: "Anniversary Couple Bottle", desc: "Romantic silhouette couple with red roses and gemstone accents.", category: "bottle" },
  { src: bottlePurpleHeart, title: "Purple Heart Bottle", desc: "Amethyst jewelled heart with delicate silver vine patterns.", category: "bottle" },
  { src: bottleGoldNames, title: "Gold Wedding Names Bottle", desc: "Personalised gold floral wreath with couple's names.", category: "bottle" },
  { src: bottleRedHearts, title: "Red Hearts & Gold Bottle", desc: "Twin ruby hearts with ornate gold filigree flourishes.", category: "bottle" },
  { src: bottleCollection, title: "Bottle Art Collection", desc: "A showcase of our handcrafted bottle art designs.", category: "bottle" },
  { src: gallery2, title: "Wedding Watercolour Portrait", desc: "Romantic watercolour portrait from your wedding photo.", category: "painting" },
  { src: gallery4, title: "Custom Pet Portrait", desc: "A vibrant acrylic portrait of your beloved pet.", category: "painting" },
  { src: gallery6, title: "Family Watercolour Portrait", desc: "A warm family portrait in soft watercolour tones.", category: "painting" },
];

const filters = ["All", "Bottle Art", "Paintings", "Custom Requests"];

const Gallery = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [lightbox, setLightbox] = useState<number | null>(null);

  const filtered = activeFilter === "All"
    ? galleryItems
    : activeFilter === "Bottle Art"
    ? galleryItems.filter((i) => i.category === "bottle")
    : activeFilter === "Paintings"
    ? galleryItems.filter((i) => i.category === "painting")
    : galleryItems;

  return (
    <Layout>
      <SEO title="Gallery · Soft Chaos Studio" description="Browse our gallery of custom portraits, hand-painted bottles & personalised gifts." canonical="/gallery" />
      <section className="container py-16">
        <h1 className="text-4xl font-serif font-semibold text-center mb-3">Gallery</h1>
        <p className="text-center text-muted-foreground mb-10">Browse our handmade creations and find inspiration for your gift.</p>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {filters.map((f) => (
            <Button
              key={f}
              variant={activeFilter === f ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveFilter(f)}
            >
              {f}
            </Button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {filtered.map((item) => {
            const globalIndex = galleryItems.indexOf(item);
            return (
            <div key={globalIndex} className="bg-card rounded-lg overflow-hidden shadow-card hover:shadow-hover transition-shadow duration-300">
              <button
                onClick={() => setLightbox(globalIndex)}
                className="w-full"
              >
                <img src={item.src} alt={item.title} className="w-full aspect-square object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
              </button>
              <div className="p-5">
                <h3 className="font-serif font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground mb-2">{item.desc}</p>
                
                <Link to="/quote">
                  <Button size="sm" variant="outline">Request a Quote</Button>
                </Link>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-50 bg-foreground/70 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-3xl w-full animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-card flex items-center justify-center shadow-card z-10"
              aria-label="Close lightbox"
            >
              <X size={20} />
            </button>
            <img
              src={galleryItems[lightbox].src}
              alt={galleryItems[lightbox].title}
              className="w-full rounded-lg shadow-card"
            />
            <div className="bg-card rounded-b-lg p-4">
              <h3 className="font-serif font-semibold">{galleryItems[lightbox].title}</h3>
              <p className="text-sm text-muted-foreground">{galleryItems[lightbox].desc}</p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Gallery;
